import { useQuery } from "@apollo/client";
import { useEffect } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";

import { Spinner } from "../../../../components/ui";
import { FETCH_INSTANCE_ID_BY_REFERENCE as fetchInstanceIdByReference } from "../../../../graphql/query/Instance";
import { instanceFilterStore } from "../../components/useInstanceFilters";
import Wizard from "./index";

// Instance ids in the URL are numeric. A non-numeric segment (e.g. "JJ71178")
// is treated as an instance reference and resolved to the real id.
export const isNumericInstanceId = value => /^\d+$/.test(String(value ?? ""));

// Target detail URL once a reference has been resolved to a numeric id. Any
// sub-path (e.g. /instance/JJ71178/history) is preserved; otherwise we land on
// the details tab.
export const buildInstanceTargetPath = (id, subPath) =>
  subPath ? `/instance/${id}/${subPath}` : `/instance/${id}/details`;

// Instance search list, used when a reference resolves to more than one match.
export const buildInstanceSearchPath = () => "/instance";

// Decide where a `/instance/<reference>` URL should land once the reference has
// been resolved. Exactly one match redirects straight to that instance; more
// than one sends the user to the search list (pre-filtered by reference);
// anything else is treated as "not found".
export const resolveInstanceReferenceDestination = ({
  count,
  matchId,
  subPath,
} = {}) => {
  if (count === 1 && matchId != null) {
    return { type: "instance", to: buildInstanceTargetPath(matchId, subPath) };
  }

  if (count > 1) {
    return { type: "search", to: buildInstanceSearchPath() };
  }

  return { type: "notFound" };
};

const InstanceReferenceRedirect = ({ reference, subPath }) => {
  const navigate = useNavigate();
  const { data, loading } = useQuery(fetchInstanceIdByReference, {
    variables: {
      input: {
        name: { value: reference, operator: "eq" },
        limit: 1,
      },
    },
  });

  const count = data?.instancesV2?.count;
  const matchId = data?.instancesV2?.edges?.[0]?.id;
  const destination = loading
    ? { type: "loading" }
    : resolveInstanceReferenceDestination({ count, matchId, subPath });

  useEffect(() => {
    if (destination.type === "search") {
      // Seed the shared filter so the list shows only the matching references.
      instanceFilterStore.set({ name: { value: reference, operator: "eq" } });
      navigate(destination.to, { replace: true });
    }
  }, [destination.type, destination.to, navigate, reference]);

  if (destination.type === "loading" || destination.type === "search") {
    return (
      <div className="flex w-full flex-1 items-center justify-center">
        <Spinner />
      </div>
    );
  }

  if (destination.type === "notFound") {
    return (
      <div className="flex w-full flex-1 items-center justify-center">
        <div className="text-red-600">
          {`No instance found for reference “${reference}”.`}
        </div>
      </div>
    );
  }

  return <Navigate to={destination.to} replace />;
};

// Routed at `:id/*`. Numeric ids render the instance wizard directly; a
// reference is looked up and redirected to its numeric detail page.
const WizardRoute = props => {
  const params = useParams();
  const reference = params?.id;
  const subPath = params?.["*"];

  if (isNumericInstanceId(reference)) {
    return <Wizard {...props} />;
  }

  return <InstanceReferenceRedirect reference={reference} subPath={subPath} />;
};

export default WizardRoute;
